import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  StarIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ClockIcon,
  MapPinIcon,
  SparklesIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { supabase, getAdminStatusCached } from './lib/supabase';
import Header from './Header';
import Footer from './Footer';
import WhatsAppButton from './WhatsAppButton';

function HomePage() {
  const [user, setUser] = useState(null);
  const [, setUserProfile] = useState(null);
  const [activeMembership, setActiveMembership] = useState(null);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    // Initial session check
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          await fetchUserProfile(session.user.email);
          await fetchActiveMembership(session.user.id);
        }
      } catch (error) {
        console.error('Initial session check error:', error);
      }
    };
    initAuth();
    fetchAnnouncements();

    // Auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED') return;

      if (session?.user) {
        setUser(session.user);
        await fetchUserProfile(session.user.email);
        await fetchActiveMembership(session.user.id);
      } else {
        setUser(null);
        setUserProfile(null);
        setActiveMembership(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (email) => {
    const { data, error } = await getAdminStatusCached(email);
    if (!error && data?.[0]) {
      setUserProfile(data[0]);
    }
  };

  const fetchActiveMembership = async (userId) => {
    try {
      const { data, error } = await supabase
        .rpc('get_active_membership', { user_uuid: userId });

      if (!error && data?.[0]) {
        setActiveMembership(data[0]);
      } else {
        setActiveMembership(null);
      }
    } catch (error) {
      console.error('Error fetching membership:', error);
      setActiveMembership(null);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
        
      if (!error && data) {
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const highlights = [
    { icon: StarIcon, label: 'Premium Court' },
    { icon: ClockIcon, label: '5 AM – 9 PM' },
    { icon: MapPinIcon, label: 'Urapakkam, Chennai' },
    { icon: ShieldCheckIcon, label: 'Open All 7 Days' },
  ];

  const gallery = [
    { src: '/images/gallery/court1.png', title: 'Court View', desc: 'Professional court with quality flooring and clear markings' },
    { src: '/images/gallery/court2.jpeg', title: 'Good Lighting', desc: 'Well-lit court ensuring perfect visibility for all your games' },
    { src: '/images/gallery/court3.jpg', title: 'Huge Area', desc: 'Spacious facility with ample room for comfortable gameplay' }
  ];

  const aboutPoints = [
    {
      icon: StarIcon,
      title: 'Premium Quality',
      desc: 'Professional-grade court and equipment designed to meet international standards for the best playing experience.'
    },
    {
      icon: UserGroupIcon,
      title: 'Vibrant Community',
      desc: 'Join sports enthusiasts and competitive players. Regular events and coaching sessions for all levels.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Always Reliable',
      desc: 'Consistent service quality and well-maintained facilities you can trust, open all 7 days.'
    }
  ];

  const membershipPlans = [
    {
      type: 'Pay per Hour',
      price: '₹300',
      unit: '/hour',
      features: ['Maximum 6 persons', 'Standard slot booking (10AM-4PM) on weekdays'],
      popular: false,
      buttonText: 'Book Now',
      link: '/booking'
    },
    {
      type: 'Monthly Membership',
      price: '₹1,200',
      unit: '/month',
      features: ['Exclusive booking slots (5-9AM & 6-9PM)', 'Priority access during peak hours'],
      popular: true,
      buttonText: 'Get Membership',
      link: '/membership'
    },
    {
      type: 'Coaching Fees',
      price: '₹2,500',
      unit: '/month',
      features: ['Professional coaching sessions', 'Exclusive booking slots 5-6 PM (Kids)', 'Classes held Monday to Friday'],
      popular: false,
      buttonText: 'Get Coaching',
      link: '/membership'
    },
    {
      type: 'Yearly Membership',
      price: '₹12,000',
      unit: '/year',
      features: ['Exclusive booking slots (5-9AM & 6-9PM)', 'Priority access during peak hours', 'Annual savings (17% off)'],
      popular: false,
      buttonText: 'Get Membership',
      link: '/membership'
    }
  ];

  const facilities = [
    { label: 'Premium Quality Court', icon: SparklesIcon },
    { label: 'Professional Lighting', icon: StarIcon },
    { label: 'Changing Rooms', icon: ShieldCheckIcon },
    { label: 'First Aid Facility', icon: CheckCircleIcon },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Announcement Banner */}
      {announcements.length > 0 && (
        <div className="bg-gradient-to-r from-red-600 to-red-500 text-white py-2 sm:py-2.5 overflow-hidden">
          <div className="whitespace-nowrap animate-scroll">
            <span className="inline-block px-4 text-xs sm:text-sm font-medium">
              {announcements.map(ann => ann.content).join('  •  ')}
            </span>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[80vh] flex flex-col overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105 animate-hero-slide"
          style={{ backgroundImage: 'url(/images/gallery/court3.jpg)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />

        {/* Center Content */}
        <div className="relative z-10 flex-1 flex items-center justify-center text-center px-4 py-10 sm:py-0">
          <div className="max-w-3xl w-full">
            <img
              src="/images/SquareLogo.jpg"
              alt="Best Badminton Club"
              className="h-14 w-14 sm:h-20 sm:w-20 rounded-xl sm:rounded-2xl shadow-2xl mx-auto mb-4 sm:mb-6 ring-2 ring-white/20 animate-smooth-rise"
            />
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-3 sm:mb-4 leading-tight animate-smooth-rise animate-delay-100">
              Best Badminton Club
            </h1>
            <p className="text-sm sm:text-lg md:text-xl text-white/70 mb-6 sm:mb-8 max-w-xl mx-auto leading-relaxed animate-smooth-rise animate-delay-200 px-2">
              Premium badminton court with world-class facilities and exceptional service in Urapakkam, Chennai.
            </p>
            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 justify-center animate-smooth-rise animate-delay-300 px-4 sm:px-0">
              <Link 
                to="/booking" 
                className="bg-green-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-lg hover:bg-green-700 transition-all shadow-lg shadow-green-600/30 inline-flex items-center justify-center gap-2 group"
              >
                Book a Court
                <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a 
                href="#membership" 
                className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-lg hover:bg-white/20 transition-all inline-flex items-center justify-center"
              >
                View Plans
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Info Bar */}
        <div className="relative z-10 mt-auto">
          <div className="bg-white/10 backdrop-blur-lg border-t border-white/10">
            <div className="container mx-auto px-2 sm:px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
                {highlights.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-center justify-center gap-1.5 sm:gap-2.5 py-3 sm:py-4 md:py-5">
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 flex-shrink-0" />
                      <span className="text-[11px] sm:text-sm font-medium text-white/80">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-green-700 to-green-800 text-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            {/* Left */}
            <div className="lg:w-1/2 animate-smooth-rise">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 sm:px-4 py-1.5 mb-4 sm:mb-5">
                <SparklesIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-300" />
                <span className="text-green-200 text-xs sm:text-sm font-medium">Since 2025</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">About Best Badminton Club</h2>
              <p className="text-sm sm:text-base text-green-100 leading-relaxed mb-6 sm:mb-8">
                Located in the heart of Urapakkam, Chennai, we provide world-class badminton facilities
                with professional court, premium equipment, and a welcoming environment for
                players of all skill levels.
              </p>
              <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
                  <div className="text-xl sm:text-2xl font-bold">7</div>
                  <div className="text-[10px] sm:text-xs text-green-200 mt-0.5 sm:mt-1">Days Open</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
                  <div className="text-xl sm:text-2xl font-bold">17hrs</div>
                  <div className="text-[10px] sm:text-xs text-green-200 mt-0.5 sm:mt-1">Daily Access</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
                  <div className="text-xl sm:text-2xl font-bold">100+</div>
                  <div className="text-[10px] sm:text-xs text-green-200 mt-0.5 sm:mt-1">Happy Players</div>
                </div>
              </div>
            </div>

            {/* Right */}
            <div className="lg:w-1/2 space-y-3 sm:space-y-4 animate-smooth-rise animate-delay-200">
              {aboutPoints.map((point, i) => {
                const Icon = point.icon;
                return (
                  <div key={i} className="flex items-start gap-3 sm:gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-5 hover:bg-white/15 transition-all">
                    <div className="bg-green-500 w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm sm:text-base mb-1">{point.title}</h3>
                      <p className="text-xs sm:text-sm text-green-100 leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-8 sm:mb-12 gap-3 sm:gap-4 animate-smooth-rise">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">Our Facilities</h2>
              <p className="text-sm sm:text-base text-gray-400 max-w-md">
                World-class court and premium amenities for every player
              </p>
            </div>
            <Link to="/booking" className="text-green-700 font-semibold text-xs sm:text-sm hover:text-green-800 transition-colors inline-flex items-center gap-1 group">
              Book a court
              <ArrowRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
            {gallery.map((image, index) => (
              <div
                key={index}
                className={`relative rounded-xl sm:rounded-2xl overflow-hidden group cursor-pointer ${
                  index === 0 ? 'md:flex-[2]' : 'md:flex-1'
                } animate-smooth-rise`}
                style={{ animationDelay: `${(index + 1) * 100}ms` }}
              >
                <img 
                  src={image.src} 
                  alt={image.title}
                  className="w-full h-[200px] sm:h-[240px] md:h-[320px] object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                  <h3 className="text-base sm:text-lg font-bold text-white mb-0.5">{image.title}</h3>
                  <p className="text-white/70 text-xs sm:text-sm">{image.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section id="membership" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-900 to-gray-950 text-white">
        <div className="container mx-auto px-4">
          {user && activeMembership ? (
            /* Active Membership */
            <div className="max-w-3xl mx-auto text-center animate-smooth-rise">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Your Active Membership</h2>
              <p className="text-sm sm:text-base text-gray-400 mb-6 sm:mb-8">Enjoy your exclusive member benefits</p>

              <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-8 mb-6 text-left">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
                  <div>
                    <div className="text-green-200 text-xs sm:text-sm font-medium mb-1">Active Plan</div>
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold capitalize">{activeMembership.membership_type} Membership</h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 bg-white/10 rounded-xl p-3 sm:p-4">
                  <div>
                    <div className="text-[10px] sm:text-xs text-green-200 mb-0.5">Expires On</div>
                    <div className="text-sm sm:text-base font-semibold">
                      {new Date(activeMembership.end_date).toLocaleDateString('en-US', {
                        month: 'long', day: 'numeric', year: 'numeric'
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] sm:text-xs text-green-200 mb-0.5">Days Remaining</div>
                    <div className="text-sm sm:text-base font-semibold">{activeMembership.days_remaining} days</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 justify-center">
                <Link
                  to="/booking"
                  className="inline-flex items-center justify-center px-5 sm:px-6 py-3 sm:py-3.5 bg-green-600 text-white rounded-xl font-semibold text-sm sm:text-base hover:bg-green-700 transition-colors shadow-sm"
                >
                  Book Your Court
                </Link>
                <Link
                  to="/membership"
                  className="inline-flex items-center justify-center px-5 sm:px-6 py-3 sm:py-3.5 bg-white/10 text-white rounded-xl font-semibold text-sm sm:text-base border border-white/20 hover:bg-white/20 transition-colors"
                >
                  View Membership Details
                </Link>
              </div>
            </div>
          ) : (
            /* Membership Plans */
            <div>
              <div className="text-center mb-8 sm:mb-14 animate-smooth-rise">
                <div className="inline-flex items-center gap-2 bg-green-600/20 rounded-full px-3 sm:px-4 py-1.5 mb-4 sm:mb-5">
                  <SparklesIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-400" />
                  <span className="text-green-300 text-xs sm:text-sm font-medium">Membership Plans</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">Choose Your Perfect Plan</h2>
                <p className="text-sm sm:text-base text-gray-400 max-w-xl mx-auto px-2">
                  Flexible options for every player — from casual bookings to premium memberships
                </p>
              </div>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 max-w-6xl mx-auto">
                {membershipPlans.map((plan, index) => (
                  <div
                    key={index}
                    className={`rounded-xl sm:rounded-2xl relative flex flex-col h-full overflow-hidden transition-all hover:-translate-y-1 animate-smooth-rise ${
                      plan.popular
                        ? 'bg-green-600 text-white ring-2 ring-green-400'
                        : 'bg-white/5 border border-white/10 hover:border-white/20'
                    }`}
                    style={{ animationDelay: `${(index + 1) * 100}ms` }}
                  >
                    {plan.popular && (
                      <div className="bg-green-500 text-center text-[10px] sm:text-xs font-bold uppercase tracking-wider py-1 sm:py-1.5">
                        Most Popular
                      </div>
                    )}

                    <div className="p-3.5 sm:p-6 flex flex-col flex-1">
                      <div className="mb-3 sm:mb-5">
                        <h3 className={`text-sm sm:text-lg font-bold mb-2 sm:mb-3 ${plan.popular ? 'text-white' : 'text-white'}`}>{plan.type}</h3>
                        <div className="flex items-baseline gap-0.5 sm:gap-1">
                          <span className={`text-xl sm:text-3xl font-bold ${plan.popular ? 'text-white' : 'text-green-400'}`}>{plan.price}</span>
                          <span className={`text-[10px] sm:text-sm ${plan.popular ? 'text-green-100' : 'text-gray-500'}`}>{plan.unit}</span>
                        </div>
                      </div>

                      <ul className="space-y-1.5 sm:space-y-2.5 mb-4 sm:mb-6 flex-grow">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-1.5 sm:gap-2">
                            <CheckCircleSolidIcon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5 ${plan.popular ? 'text-green-200' : 'text-green-500'}`} />
                            <span className={`text-[11px] sm:text-sm leading-tight ${plan.popular ? 'text-green-50' : 'text-gray-400'}`}>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <Link 
                        to={plan.link}
                        className={`w-full block text-center py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all ${
                          plan.popular
                            ? 'bg-white text-green-700 hover:bg-green-50'
                            : 'bg-white/10 text-white hover:bg-white/15 border border-white/10'
                        }`}
                      >
                        {plan.buttonText}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-8 sm:mt-10 animate-smooth-rise animate-delay-500">
                <Link to="/membership" className="text-green-400 font-semibold text-xs sm:text-sm hover:text-green-300 transition-colors inline-flex items-center gap-1 group">
                  View all membership details
                  <ArrowRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Facilities */}
      <section id="facilities" className="py-10 sm:py-14 md:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12">
            <div className="md:w-1/3 text-center md:text-left">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Our Amenities</h2>
              <p className="text-gray-400 text-xs sm:text-sm">Everything for an exceptional experience</p>
            </div>
            <div className="md:w-2/3 grid grid-cols-2 gap-2.5 sm:gap-4 w-full">
              {facilities.map((facility, index) => {
                return (
                  <div key={index} className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <CheckCircleSolidIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                    <span className="font-medium text-gray-700 text-xs sm:text-sm">{facility.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}

export default HomePage;
